'use client'

import React, { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
  Filter,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpDown
} from 'lucide-react'
import { format } from 'date-fns'
import { ko } from 'date-fns/locale'

interface AlertLog {
  id: string
  patientId: string
  patientName: string
  ward: string
  room: string
  riskScore: number
  riskLevel: string
  threshold: number
  timestamp: string
  alertType: string
  status: string
  actionTaken: boolean
  actionTime: string | null
  actionDetails: string | null
  responseTime: number
}

interface AlertLogSummary {
  totalAlerts: number
  avgResponseTime: number
  actionRate: number
  statusBreakdown: {
    acknowledged: number
    escalated: number
    resolved: number
    pending: number
  }
}

interface AlertLogsTableProps {
  wardId?: string
}

export function AlertLogsTable({ wardId }: AlertLogsTableProps) {
  const [data, setData] = useState<AlertLog[]>([])
  const [summary, setSummary] = useState<AlertLogSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })

  // Filters
  const [selectedWard, setSelectedWard] = useState<string>(wardId || 'all')
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string>('all')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [exporting, setExporting] = useState(false)

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(pagination.pageIndex + 1),
        limit: String(pagination.pageSize),
      })

      if (selectedWard && selectedWard !== 'all') params.append('ward', selectedWard)
      if (selectedRiskLevel && selectedRiskLevel !== 'all') params.append('risk_level', selectedRiskLevel)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await fetch(`/api/v1/alerts/logs?${params}`)
      const result = await response.json()

      setData(result.logs)
      setSummary(result.summary)
    } catch (error) {
      console.error('Failed to fetch alert logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const exportToCSV = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams()
      if (selectedWard && selectedWard !== 'all') params.append('ward', selectedWard)
      if (selectedRiskLevel && selectedRiskLevel !== 'all') params.append('risk_level', selectedRiskLevel)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await fetch(`/api/v1/alerts/logs/export.csv?${params}`)
      const blob = await response.blob()

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `alert_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to export CSV:', error)
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    fetchLogs()
  }, [pagination.pageIndex, pagination.pageSize, selectedWard, selectedRiskLevel, startDate, endDate])

  const columns: ColumnDef<AlertLog>[] = [
    {
      accessorKey: 'timestamp',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          시간
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-mono text-sm">
          {format(new Date(row.getValue('timestamp')), 'MM/dd HH:mm', { locale: ko })}
        </div>
      ),
    },
    {
      accessorKey: 'patientName',
      header: '환자명',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('patientName')}</div>
      ),
    },
    {
      accessorKey: 'room',
      header: '병실',
    },
    {
      accessorKey: 'riskScore',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          위험도
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const score = row.getValue('riskScore') as number
        return (
          <div className="flex items-center gap-2">
            <div className="font-semibold">
              {(score * 100).toFixed(1)}%
            </div>
            <Badge variant={score > 0.75 ? 'destructive' : score > 0.5 ? 'secondary' : 'outline'}>
              {row.original.riskLevel === 'high' ? '고위험' :
               row.original.riskLevel === 'medium' ? '중간' : '저위험'}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: 'alertType',
      header: '알림 유형',
      cell: ({ row }) => {
        const type = row.getValue('alertType') as string
        const typeMap: Record<string, string> = {
          respiratory: '호흡억제',
          hypotension: '저혈압',
          combined: '복합'
        }
        return typeMap[type] || type
      },
    },
    {
      accessorKey: 'status',
      header: '상태',
      cell: ({ row }) => {
        const status = row.getValue('status') as string
        const statusConfig = {
          acknowledged: { label: '확인됨', icon: CheckCircle, variant: 'secondary' as const },
          escalated: { label: '상위보고', icon: AlertCircle, variant: 'destructive' as const },
          resolved: { label: '해결됨', icon: CheckCircle, variant: 'outline' as const },
          pending: { label: '대기중', icon: Clock, variant: 'secondary' as const }
        }
        const config = statusConfig[status as keyof typeof statusConfig]
        const Icon = config?.icon || Clock

        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <Badge variant={config?.variant || 'secondary'}>
              {config?.label || status}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: 'actionTaken',
      header: '조치',
      cell: ({ row }) => {
        const taken = row.getValue('actionTaken') as boolean
        return taken ? (
          <Badge variant="outline" className="bg-green-50">조치완료</Badge>
        ) : (
          <Badge variant="secondary">미조치</Badge>
        )
      },
    },
    {
      accessorKey: 'responseTime',
      header: '응답시간',
      cell: ({ row }) => {
        const time = row.getValue('responseTime') as number
        return time > 0 ? `${time}분` : '-'
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>알림 기록</CardTitle>
            <CardDescription>
              과거 알림 및 조치 기록을 조회합니다
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchLogs}
              variant="outline"
              size="sm"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? '내보내는 중...' : 'CSV 내보내기'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg bg-muted/50">
          <div className="space-y-2">
            <Label htmlFor="ward">병동</Label>
            <Select value={selectedWard} onValueChange={setSelectedWard}>
              <SelectTrigger id="ward">
                <SelectValue placeholder="전체 병동" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체</SelectItem>
                <SelectItem value="3A">3A</SelectItem>
                <SelectItem value="3B">3B</SelectItem>
                <SelectItem value="3C">3C</SelectItem>
                <SelectItem value="4A">4A</SelectItem>
                <SelectItem value="4B">4B</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="risk">위험도</Label>
            <Select value={selectedRiskLevel} onValueChange={setSelectedRiskLevel}>
              <SelectTrigger id="risk">
                <SelectValue placeholder="전체 위험도" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체</SelectItem>
                <SelectItem value="high">고위험</SelectItem>
                <SelectItem value="medium">중간</SelectItem>
                <SelectItem value="low">저위험</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="startDate">시작일</Label>
            <Input
              id="startDate"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="endDate">종료일</Label>
            <Input
              id="endDate"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">총 알림</div>
              <div className="text-2xl font-bold">{summary.totalAlerts}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">평균 응답시간</div>
              <div className="text-2xl font-bold">{summary.avgResponseTime.toFixed(1)}분</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">조치율</div>
              <div className="text-2xl font-bold">{summary.actionRate.toFixed(1)}%</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">대기중</div>
              <div className="text-2xl font-bold text-orange-600">
                {summary.statusBreakdown.pending}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center h-32">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    데이터를 불러오는 중...
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center h-32">
                    데이터가 없습니다
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            총 {summary?.totalAlerts || 0}개 중 {pagination.pageIndex * pagination.pageSize + 1} - {Math.min((pagination.pageIndex + 1) * pagination.pageSize, summary?.totalAlerts || 0)}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm">
              페이지 {pagination.pageIndex + 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}